import React, { useState, useEffect } from 'react';

import { extractZip, haversineMiles, lookupZipCentroid } from '../../../util/usZip';

import css from './DeliveryRangeCheck.module.css';

// Delivery/event-address entry + service-range check.
//
// Geocoding-free by design: instead of live Mapbox full-address geocoding
// (fragile script-load/SSR timing — see history), the address is plain
// text validated to contain a 5-digit US ZIP, and the range check uses a
// static ZIP→centroid table (lazy-loaded). The structured pieces (zip +
// city/state + centroid) are handed up so checkout can prefill the
// event-address fields without anything ever resolving to a map pin.
const DeliveryRangeCheck = ({
  listingGeolocation,
  listingZip,
  radiusMiles,
  onAddressChange,
  addressLabel = 'Delivery address',
  helperText = 'Address where the kit will be delivered',
}) => {
  const [query, setQuery] = useState('');
  const [touched, setTouched] = useState(false);
  const [hostPoint, setHostPoint] = useState(
    listingGeolocation?.lat && listingGeolocation?.lng
      ? { lat: listingGeolocation.lat, lng: listingGeolocation.lng }
      : null
  );
  const [distance, setDistance] = useState(null);

  const zip = extractZip(query);
  const hasZip = zip != null;
  const showError = touched && query.trim().length > 0 && !hasZip;

  // Lazily load the centroid table the first time a ZIP appears, then
  // resolve the renter's point (for carry-over + range) and, if needed,
  // the host's point from its ZIP fallback.
  useEffect(() => {
    if (!hasZip) {
      setDistance(null);
      if (onAddressChange) onAddressChange(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const renter = await lookupZipCentroid(zip);
        if (cancelled) return;

        // Host point: prefer the listing's real geolocation; else fall back
        // to the host ZIP centroid. May stay null (range line just hidden).
        let host = hostPoint;
        if (!host && listingZip) {
          const hz = await lookupZipCentroid(listingZip);
          if (hz) host = { lat: hz.lat, lng: hz.lng };
          if (!cancelled && host) setHostPoint(host);
        }

        const d =
          host && renter ? haversineMiles(host, { lat: renter.lat, lng: renter.lng }) : null;
        if (cancelled) return;
        setDistance(d);

        // Hand the structured address up for checkout carry-over. Street
        // text is whatever the renter typed (editable at checkout); city/
        // state/zip come from the centroid table; origin is the centroid.
        if (onAddressChange) {
          onAddressChange({
            address: query.trim(),
            zip,
            city: renter?.city || '',
            state: renter?.state || '',
            origin: renter ? { lat: renter.lat, lng: renter.lng } : null,
          });
        }
      } catch (e) {
        // Table load failed → informational fallback: still carry the
        // address+zip (so checkout/host get it), just no distance line.
        if (cancelled) return;
        console.error('ZIP centroid lookup failed:', e);
        setDistance(null);
        if (onAddressChange) {
          onAddressChange({ address: query.trim(), zip, city: '', state: '', origin: null });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [zip, hasZip, listingZip]); // eslint-disable-line

  const canShowRange = distance != null && !!radiusMiles;
  const withinRange = canShowRange && distance <= radiusMiles;

  return (
    <div className={css.root}>
      <label className={css.label}>{addressLabel}</label>
      <p className={css.helper}>{helperText}</p>
      <input
        type="text"
        className={css.input}
        placeholder="Street address, city, and 5-digit ZIP"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onBlur={() => setTouched(true)}
        aria-invalid={showError}
      />
      {showError && (
        <p className={css.error}>
          Enter a full address including a 5-digit ZIP code.
        </p>
      )}
      {canShowRange && (
        <p className={withinRange ? css.ok : css.warn}>
          {withinRange
            ? `✅ Within service range (${distance.toFixed(1)} mi away, host serves up to ${radiusMiles} mi)`
            : `⚠️ You're about ${distance.toFixed(1)} miles away. This host serves within ${radiusMiles} miles. You can still request the booking and the host can decide.`}
        </p>
      )}
      <p className={css.hint}>
        {radiusMiles
          ? `Host serves within ${radiusMiles} miles. Bookings outside the range still work, but the host may decline or adjust the fee.`
          : 'Required to request this booking.'}
      </p>
    </div>
  );
};

export default DeliveryRangeCheck;
